/**
 * SystemConfigModal - Modale per configurare impostazioni di sistema
 * (es. email notifica pagamento, altre config future)
 */

import { useState, useEffect, useCallback } from 'react';
import { X, Save, Loader2, Mail } from 'lucide-react';

interface SystemConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ConfigEntry {
  config_key: string;
  config_value: string;
  description: string;
}

const CONFIG_META: Record<string, { label: string; icon: typeof Mail; placeholder: string }> = {
  notification_email_pagamento: {
    label: 'Email notifica pagamento',
    icon: Mail,
    placeholder: 'email@esempio.com',
  },
};

export default function SystemConfigModal({ isOpen, onClose }: SystemConfigModalProps) {
  const [configs, setConfigs] = useState<ConfigEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/system-config');
      if (res.ok) {
        const data = await res.json();
        setConfigs(data.configs || []);
        const vals: Record<string, string> = {};
        for (const c of data.configs || []) {
          vals[c.config_key] = c.config_value || '';
        }
        setEditValues(vals);
      }
    } catch (err) {
      console.error('Errore fetch system-config:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchConfigs();
      setSaveSuccess(null);
    }
  }, [isOpen, fetchConfigs]);

  const handleSave = async (key: string) => {
    setSaving(key);
    try {
      const res = await fetch(`/api/admin/system-config/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: editValues[key] }),
      });
      if (res.ok) {
        setSaveSuccess(key);
        setTimeout(() => setSaveSuccess(null), 2000);
      }
    } catch (err) {
      console.error('Errore salvataggio config:', err);
    } finally {
      setSaving(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-800 border border-slate-600 rounded-xl shadow-2xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Impostazioni Sistema</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-teal-400 animate-spin" />
              <span className="ml-2 text-sm text-gray-400">Caricamento...</span>
            </div>
          ) : configs.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Nessuna configurazione trovata</p>
          ) : (
            configs.map((config) => {
              const meta = CONFIG_META[config.config_key];
              const Icon = meta?.icon || Mail;
              const changed = editValues[config.config_key] !== config.config_value;

              return (
                <div key={config.config_key} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4 text-teal-400" />
                    <span className="text-sm font-medium text-white">
                      {meta?.label || config.config_key}
                    </span>
                  </div>
                  {config.description && (
                    <p className="text-xs text-gray-400 mb-3">{config.description}</p>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editValues[config.config_key] || ''}
                      onChange={(e) => setEditValues(prev => ({ ...prev, [config.config_key]: e.target.value }))}
                      placeholder={meta?.placeholder || ''}
                      className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-teal-400"
                    />
                    <button
                      onClick={() => handleSave(config.config_key)}
                      disabled={!changed || saving === config.config_key}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                        saveSuccess === config.config_key
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : changed
                            ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30 hover:bg-teal-500/30'
                            : 'bg-slate-700 text-gray-500 border border-slate-600 cursor-not-allowed'
                      }`}
                    >
                      {saving === config.config_key ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : saveSuccess === config.config_key ? (
                        'Salvato'
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Salva
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
